import { useTranslation, Trans } from "react-i18next";
import { useMemo, useState } from "react";
import {
  IconUsersGroup,
  IconLink,
  IconChevronDown,
  IconCheck,
} from "@tabler/icons-react";
import { Button } from "@/components/lilia-ui/button";
import { Badge } from "@/components/lilia-ui/badge";
import { Spinner } from "@/components/lilia-ui/spinner";
import { Input } from "@/components/lilia-ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/lilia-ui/select";
import { useTeams, useTeamMembers } from "@/hooks/useTeams";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import { useSharingApi } from "@/hooks/useSharingApi";
import type { UserCollaborator } from "@/lib/api";
import { ASSIGNABLE_ROLES, ROLE_LABELS } from "@/lib/sharing/roleLabels";
import {
  buildSharingError,
  classifyShareError,
  type SharingError,
  type SharingErrorAction,
} from "@/lib/sharing/errors";
import { SharingErrorBanner } from "../parts/SharingErrorBanner";

interface Props {
  documentId: string;
  attachedTeamId?: string | null;
  attachedTeamName?: string | null;
  /** Iter 10 — known doc collaborators, used to dedupe the
   *  per-member picker against already-added rows. The picker
   *  marks dedupes with an "Already added" pill instead of
   *  hiding them, so the user always sees who's in the team. */
  collaborators?: UserCollaborator[];
  /** Iter 10 — owner is implicit and shouldn't appear as a
   *  pickable member (they're not granting themselves access). */
  ownerId?: string;
  onChange: () => void;
}

// Spec §5 (iter 1) + Claude Design follow-up 2026-05-17
// (iter 10) — Team access has three master-level states
// (attached / default-team discovery / picker) PLUS a stacked
// per-member quickpick block below.
//
// Picker rules:
//   - Source: the doc's attached team if any, else the first
//     team from useTeams() (typically the user's default team).
//   - When the doc IS team-attached (master on), the per-member
//     rows render checked-disabled-greyed with an
//     "included via team attachment" tooltip — picking
//     individuals is redundant because the team already grants
//     access.
//   - When NOT attached, the user can tick members and click
//     "Add N" — each tick becomes a per-user collaborator row
//     (POST /api/documents/{id}/collaborators/users). New team
//     members later aren't auto-shared (which is what the user
//     wanted: subset, not whole-team).
//   - Filter input appears at >8 members.
//   - Dedupe against the doc's existing collaborators with an
//     "Already added" pill — single source of truth for role
//     is the Invite-people row.
export function TeamAccessSection({
  documentId,
  attachedTeamId,
  attachedTeamName,
  collaborators,
  ownerId,
  onChange,
}: Props) {
  const { t } = useTranslation("sharing");
  const { fetchWithAuth } = useApiContext();
  const sharingApi = useSharingApi();
  const { data: teams, isLoading } = useTeams();

  // Active team for the picker: the doc's attached team if it
  // has one, otherwise the user's default (or first) team. The
  // active-team store is one alternative but it's not always
  // populated, so this falls through teams[] safely.
  const defaultTeam = useMemo(() => {
    if (!teams || teams.length === 0) return null;
    return teams.find((t) => t.isDefault) ?? teams[0];
  }, [teams]);
  const pickerTeamId = attachedTeamId ?? defaultTeam?.id ?? "";
  const pickerTeamName = attachedTeamName ?? defaultTeam?.name ?? "your team";
  const { data: pickerMembers } = useTeamMembers(pickerTeamId);

  const [picking, setPicking] = useState(false);
  const [pickedTeamId, setPickedTeamId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<SharingError | null>(null);

  // Per-member picker state.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pickRole, setPickRole] = useState<string>("editor");
  const [pickQuery, setPickQuery] = useState("");
  const [pickerBusy, setPickerBusy] = useState(false);

  async function persistTeam(teamId: string | null, teamLabel?: string) {
    setBusy(true);
    setError(null);
    try {
      const r = await fetchWithAuth(`${API_BASE}/documents/${documentId}/team`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ teamId }),
      });
      if (!r.ok) throw new Error("Failed to update team attachment");
      onChange();
      setPicking(false);
      setPickedTeamId("");
    } catch (err) {
      setError(buildSharingError(classifyShareError(err, "team-attach-failed"), { team: teamLabel }));
    } finally {
      setBusy(false);
    }
  }

  function handleErrorAction(a: SharingErrorAction) {
    if (a.kind === "detach") void persistTeam(null);
    if (a.kind === "pick-different-team") setPicking(true);
    setError(null);
  }

  const attachedMemberCount = pickerMembers?.length;

  // ---------- Picker derived data -----------------------------
  const isAttached = !!attachedTeamId;
  // Exclude the doc owner (implicit access). Sort: not-yet-added
  // first, then alphabetical by display name.
  const collabIds = useMemo(
    () => new Set((collaborators ?? []).map((c) => c.userId)),
    [collaborators],
  );
  const pickableMembers = useMemo(() => {
    if (!pickerMembers) return [];
    return pickerMembers.filter((m) => !ownerId || m.userId !== ownerId);
  }, [pickerMembers, ownerId]);
  const filteredMembers = useMemo(() => {
    const q = pickQuery.trim().toLowerCase();
    if (!q) return pickableMembers;
    return pickableMembers.filter(
      (m) =>
        (m.name ?? "").toLowerCase().includes(q) ||
        (m.email ?? "").toLowerCase().includes(q),
    );
  }, [pickableMembers, pickQuery]);
  const showFilter = pickableMembers.length > 8;

  function togglePick(userId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function commitPicks() {
    const ids = Array.from(selectedIds).filter((id) => !collabIds.has(id));
    if (ids.length === 0) return;
    setPickerBusy(true);
    setError(null);
    const results = await Promise.allSettled(
      ids.map((id) => sharingApi.addUserById(documentId, id, pickRole)),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    setPickerBusy(false);
    if (failed === 0) {
      setSelectedIds(new Set());
      onChange();
    } else {
      setError(
        buildSharingError(
          classifyShareError(new Error("partial-add"), "network"),
        ),
      );
    }
  }

  // Net new picks (not already collaborators). Counter on the
  // commit button.
  const newPickCount = useMemo(
    () => Array.from(selectedIds).filter((id) => !collabIds.has(id)).length,
    [selectedIds, collabIds],
  );

  return (
    <section data-testid="share-team-section" data-attached={attachedTeamId ? "true" : "false"} className="border-b border-[var(--lilia-border)] py-5 last:border-b-0">
      <header className="mb-3 flex items-center gap-2">
        <IconUsersGroup size={16} className="text-[var(--lilia-text-secondary)]" />
        <h3 className="text-sm font-semibold text-[var(--lilia-text)]">{t("team.title")}</h3>
        <span className="ml-auto text-[11px] text-[var(--lilia-text-muted)]">
          {t("team.onePerDoc")}
        </span>
      </header>

      {error && (
        <div className="mb-3">
          <SharingErrorBanner
            error={error}
            onAction={handleErrorAction}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* ---------- State 1: Attached ---------- */}
      {attachedTeamId && (
        <div className="flex items-center gap-3 rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-bg)] px-3 py-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[var(--lilia-primary,#4f46e5)]/10 text-xs font-semibold text-[var(--lilia-primary,#4f46e5)]">
            {(attachedTeamName ?? "T").slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-[var(--lilia-text)]">
              {attachedTeamName ?? t("team.team")}
            </p>
            <p className="truncate text-xs text-[var(--lilia-text-muted)]">
              {attachedMemberCount !== undefined
                ? t("team.members", { count: attachedMemberCount })
                : t("team.everyoneCanEdit")}
            </p>
          </div>
          <Button
            data-testid="share-team-detach"
            variant="outline"
            size="xs"
            disabled={busy}
            onClick={() => persistTeam(null, attachedTeamName ?? undefined)}
          >
            {t("team.detach")}
          </Button>
        </div>
      )}

      {/* ---------- State 2 & 3: Not attached ---------- */}
      {!attachedTeamId && isLoading && (
        <div className="flex items-center justify-center py-3">
          <Spinner />
        </div>
      )}

      {!attachedTeamId && !isLoading && (!teams || teams.length === 0) && (
        <p className="text-xs text-[var(--lilia-text-muted)]">
          {t("team.noTeams")}
        </p>
      )}

      {!attachedTeamId && !isLoading && teams && teams.length >= 1 && !picking && (
        // Discovery card — spec §13 copy block, verbatim.
        <div className="space-y-3 rounded-lg border border-dashed border-[var(--lilia-border)] bg-[var(--lilia-bg)] p-3">
          <p className="text-sm text-[var(--lilia-text)]">
            <Trans
              t={t}
              i18nKey="team.discovery"
              values={{
                name: defaultTeam?.name ?? "",
                others:
                  (defaultTeam?.memberCount ?? 1) <= 1
                    ? t("team.noOthers")
                    : t("team.others", { count: (defaultTeam?.memberCount ?? 1) - 1 }),
              }}
              components={{
                strong: (
                  <strong className="font-semibold">
                    {defaultTeam?.name}
                  </strong>
                ),
              }}
            />
            {defaultTeam?.isDefault && (
              <Badge variant="secondary" className="ml-2 align-middle">
                {t("team.default")}
              </Badge>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              data-testid="share-team-attach"
              variant="primary"
              size="sm"
              disabled={busy || !defaultTeam}
              onClick={() => defaultTeam && persistTeam(defaultTeam.id, defaultTeam.name)}
            >
              <IconLink size={14} className="mr-1" />
              {t("team.attachTo", { name: defaultTeam?.name ?? t("team.teamWord") })}
            </Button>
            {teams.length > 1 && (
              <Button variant="outline" size="sm" onClick={() => setPicking(true)}>
                <IconChevronDown size={14} className="mr-1" />
                {t("team.pickAnother")}
              </Button>
            )}
          </div>
        </div>
      )}

      {!attachedTeamId && picking && teams && teams.length > 0 && (
        <div className="space-y-2 rounded-lg border border-[var(--lilia-border)] p-3">
          <label className="block text-xs font-medium text-[var(--lilia-text-muted)]">
            {t("team.pickATeam")}
          </label>
          <Select value={pickedTeamId} onValueChange={setPickedTeamId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("team.choose")} />
            </SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                  {team.isDefault && (
                    <span className="ml-2 rounded bg-[var(--lilia-bg)] px-1.5 py-0.5 text-[10px] text-[var(--lilia-text-muted)]">
                      {t("team.default")}
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="xs" onClick={() => setPicking(false)}>
              {t("team.cancel")}
            </Button>
            <Button
              variant="primary"
              size="xs"
              disabled={!pickedTeamId || busy}
              onClick={() => {
                const team = teams.find((x) => x.id === pickedTeamId);
                if (team) persistTeam(team.id, team.name);
              }}
            >
              {t("team.attach")}
            </Button>
          </div>
        </div>
      )}

      {/* ---------- Per-member quickpick (iter 10) ---------- */}
      {pickableMembers.length > 0 && (
        <div className="mt-4 space-y-3 rounded-lg border border-[var(--lilia-border)] p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-[var(--lilia-text-muted)]">
              {isAttached ? (
                <>{t("team.alreadyHave")}</>
              ) : (
                <Trans t={t} i18nKey="team.orPick" values={{ team: pickerTeamName }} components={{ strong: <strong /> }} />
              )}
            </p>
            {showFilter && !isAttached && (
              <Input
                placeholder={t("team.filter")}
                value={pickQuery}
                onChange={(e) => setPickQuery(e.target.value)}
                className="h-7 w-32 text-xs"
              />
            )}
          </div>

          <ul className="-mx-1 max-h-[220px] overflow-y-auto px-1">
            {filteredMembers.map((m) => {
              const display = m.name ?? m.email ?? t("invite.unknown");
              const initials = display.charAt(0).toUpperCase();
              const isAlreadyCollab = collabIds.has(m.userId);
              const checked = isAttached || isAlreadyCollab || selectedIds.has(m.userId);
              const disabled = isAttached || isAlreadyCollab;
              return (
                <li
                  key={m.userId}
                  className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 ${
                    disabled ? "" : "cursor-pointer hover:bg-[var(--lilia-bg)]"
                  }`}
                  onClick={() => !disabled && togglePick(m.userId)}
                >
                  <span
                    aria-hidden
                    className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                      checked
                        ? `${
                            disabled
                              ? "border-[var(--lilia-border)] bg-[var(--lilia-bg)] text-[var(--lilia-text-muted)]"
                              : "border-[var(--lilia-primary,#4f46e5)] bg-[var(--lilia-primary,#4f46e5)] text-[color:var(--lilia-on-primary)]"
                          }`
                        : "border-[var(--lilia-border)]"
                    }`}
                  >
                    {checked && <IconCheck size={12} />}
                  </span>
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--lilia-primary,#4f46e5)]/15 text-[10px] font-semibold text-[var(--lilia-primary,#4f46e5)]">
                    {initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-[var(--lilia-text)]">{display}</p>
                    {m.email && m.name && (
                      <p className="truncate text-[11px] text-[var(--lilia-text-muted)]">
                        {m.email}
                      </p>
                    )}
                  </div>
                  {(isAttached || isAlreadyCollab) && (
                    <span className="rounded bg-[var(--lilia-bg)] px-1.5 py-0.5 text-[10px] text-[var(--lilia-text-muted)]">
                      {isAttached ? t("team.viaTeam") : t("team.alreadyAdded")}
                    </span>
                  )}
                </li>
              );
            })}
            {filteredMembers.length === 0 && (
              <li className="px-2 py-3 text-center text-xs text-[var(--lilia-text-muted)]">
                {pickQuery ? t("team.noMatch") : t("team.noOtherMembers")}
              </li>
            )}
          </ul>

          {/* Commit row — only shows when picking is meaningful
              (not attached) AND something is ticked. */}
          {!isAttached && selectedIds.size > 0 && (
            <div className="flex items-center gap-2 border-t border-[var(--lilia-border)] pt-3">
              <label className="text-[11px] text-[var(--lilia-text-muted)]">
                {t("team.defaultRole")}
              </label>
              <Select value={pickRole} onValueChange={setPickRole}>
                <SelectTrigger className="h-8 w-[110px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNABLE_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value} disabled={r.reserved}>
                      {r.label}
                      {r.reserved && (
                        <span className="ml-2 text-[10px] text-[var(--lilia-text-muted)]">
                          {t("soon")}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[11px] text-[var(--lilia-text-muted)]">
                · {ROLE_LABELS[pickRole as keyof typeof ROLE_LABELS] ?? pickRole}
              </span>
              <Button
                variant="primary"
                size="xs"
                className="ml-auto"
                onClick={commitPicks}
                disabled={pickerBusy || newPickCount === 0}
              >
                {pickerBusy ? t("team.adding") : newPickCount ? t("team.addN", { n: newPickCount }) : t("team.add")}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
